import {patchState, signalStore, withComputed, withLinkedState, withMethods, withState} from '@ngrx/signals';
import {withDevtools} from '@angular-architects/ngrx-toolkit';
import {computed, inject} from '@angular/core';
import {ApiService} from '../../../shared/services/api.service';
import {TYPE} from '../../../shared/types/types';
import {Observable, take} from 'rxjs';
import {KnowledgebaseSubPageResource} from '../../../shared/types/knowledgebase-types/knowledgebaseSubPageResource';
import {KnowledgebaseContentResource} from '../../../shared/types/knowledgebase-types/knowledgebaseContentResource';
import {KnowledgebasePageUpdateRequest} from '../../../shared/types/knowledgebase-types/knowledgebasePageUpdateRequest';
import {KnowledgebaseContentUpdateRequest} from '../../../shared/types/knowledgebase-types/knowledgebaseContentUpdateRequest';
import {KnowledgebaseMoveDirection} from '../../../shared/types/knowledgebase-types/knowledgebaseMoveDirection';
import {KnowledgebasePageAdminResource} from '../../../shared/types/knowledgebase-types/knowledgebasePageAdminResource';
import {UserMinimalResource} from '../../../shared/types/knowledgebase-types/userMinimalResource';
import {Router} from '@angular/router';
import {KnowledgebasePageTreeResource} from '../../../shared/types/knowledgebase-types/knowledgebasePageTreeResource';
import {FileStorageResource} from '../../../shared/types/settings-types/fileStorageResource';
import {DocumentService} from '../../../shared/services/document.service';
import {HttpResponse} from '@angular/common/http';
import {KnowledgebasePageResource} from '../../../shared/types/knowledgebase-types/knowledgebasePageResource';


export interface KnowledgebaseState {
  filter: {
    query: string,
    type: TYPE.KnowledgebaseFilterType,
  },
  categoryOrder: TYPE.KnowledgebaseCategoryOrder,
  fileView: TYPE.KnowledgebaseFileViewType,
  categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder,
  categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter,
  routeData: {
    categoryId: number | null,
    articleId: number | null,
  },
  rootPages: KnowledgebaseSubPageResource[];
  rootPagesTotalCount: number;
  rootPagination: {
    limit: number;
    maxReached: boolean;
  };
  pages: Map<number, KnowledgebaseSubPageResource[]>;
  pageTotalCounts: Map<number, number>;
  pageDetails: Map<number, KnowledgebasePageResource>;
  pagePagination: Map<number, {
    limit: number;
    maxReached: boolean;
  }>;
  articles: Map<number, KnowledgebaseContentResource[]>;
  articleTotalCounts: Map<number, number>;
  articlesPagination: Map<number, {
    skip: number;
    limit: number;
    maxReached: boolean;
  }>;
  files: Map<number, FileStorageResource[]>;
  fileTotalCounts: Map<number, number>;
  filesPagination: Map<number, {
    skip: number;
    limit: number;
    maxReached: boolean;
  }>;
  categoriesLoading: boolean;
  rootPagesLoading: boolean;
  pageLoading: Map<number, boolean>;
  articlesLoading: Map<number, boolean>;
  filesLoading: Map<number, boolean>;
  categoryTree: {
    entries: KnowledgebasePageTreeResource[];
    searchString: string;
  },
  quickNavOpen: boolean;
  quickNavInitialized: boolean;
  quickNavRequestId: number;
  quickNavLevelCategoryId: number | null;
  quickNavCategoryOrderApplied: TYPE.KnowledgebaseCategoryOrder;
  quickNavArticleOrderApplied: TYPE.KnowledgebaseCategoryContentOrder;
  quickNavCategories: KnowledgebaseSubPageResource[];
  quickNavCategoriesLoading: boolean;
  quickNavCategoriesError: string | null;
  quickNavCategoriesLoadedAll: boolean;
  quickNavCategoriesSkip: number;
  quickNavCategoriesLimit: number;
  quickNavArticles: KnowledgebaseContentResource[];
  quickNavArticlesLoading: boolean;
  quickNavArticlesError: string | null;
  quickNavArticlesSkip: number;
  quickNavArticlesLimit: number;
  quickNavArticlesHasMore: boolean;
}

const initialState: KnowledgebaseState = {
  filter: {
    query: '',
    type: TYPE.KnowledgebaseFilterType.ARTICLE,
  },
  categoryOrder: TYPE.KnowledgebaseCategoryOrder.PREDEFINED,
  fileView: TYPE.KnowledgebaseFileViewType.LIST,
  categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder.PREDEFINED,
  categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter.ARTICLES_ONLY,
  routeData: {
    categoryId: null,
    articleId: null,
  },
  rootPages: [],
  rootPagesTotalCount: 0,
  rootPagination: {
    limit: 20,
    maxReached: false,
  },
  pages: new Map<number, KnowledgebaseSubPageResource[]>(),
  pageTotalCounts: new Map<number, number>(),
  pageDetails: new Map<number, KnowledgebasePageResource>(),
  pagePagination: new Map<number, { limit: number,maxReached: false }>(),
  articles: new Map<number, KnowledgebaseContentResource[]>(),
  articleTotalCounts: new Map<number, number>(),
  articlesPagination: new Map<number, { skip: number, limit: number,maxReached: false }>(),
  files: new Map<number, FileStorageResource[]>(),
  fileTotalCounts: new Map<number, number>(),
  filesPagination: new Map<number, { skip: number, limit: number,maxReached: false }>(),
  categoriesLoading: false,
  rootPagesLoading: false,
  pageLoading: new Map<number, boolean>(),
  articlesLoading: new Map<number, boolean>(),
  filesLoading: new Map<number, boolean>(),
  categoryTree: {
    entries: [],
    searchString: '',
  },
  quickNavOpen: false,
  quickNavInitialized: false,
  quickNavRequestId: 0,
  quickNavLevelCategoryId: null,
  quickNavCategoryOrderApplied: TYPE.KnowledgebaseCategoryOrder.PREDEFINED,
  quickNavArticleOrderApplied: TYPE.KnowledgebaseCategoryContentOrder.PREDEFINED,
  quickNavCategories: [],
  quickNavCategoriesLoading: false,
  quickNavCategoriesError: null,
  quickNavCategoriesLoadedAll: false,
  quickNavCategoriesSkip: 0,
  quickNavCategoriesLimit: 100,
  quickNavArticles: [],
  quickNavArticlesLoading: false,
  quickNavArticlesError: null,
  quickNavArticlesSkip: 0,
  quickNavArticlesLimit: 50,
  quickNavArticlesHasMore: false,
};

function isAllowedCategorySearchOrder(categoryOrder: TYPE.KnowledgebaseCategoryOrder): boolean {
  return (
    categoryOrder === TYPE.KnowledgebaseCategoryOrder.ALPHABETICAL ||
    categoryOrder === TYPE.KnowledgebaseCategoryOrder.RECENT
  );
}

function getSearchResultFileView(
  hasSearchQuery: boolean,
  filterType: TYPE.KnowledgebaseFilterType,
  currentFileView: TYPE.KnowledgebaseFileViewType
): TYPE.KnowledgebaseFileViewType {
  if (!hasSearchQuery) {
    return currentFileView;
  }

  if (filterType === TYPE.KnowledgebaseFilterType.ARTICLE) {
    return TYPE.KnowledgebaseFileViewType.LIST;
  }

  if (filterType === TYPE.KnowledgebaseFilterType.FILE) {
    return TYPE.KnowledgebaseFileViewType.LIST;
  }

  return currentFileView;
}

export const KnowledgebaseStore = signalStore(
  {providedIn: 'root'},
  withState<KnowledgebaseState>(initialState),
  withDevtools('KnowledgebaseStore'),
  withLinkedState(({articles, files, pageDetails, routeData, rootPagination, pagePagination, articlesPagination, filesPagination, filter, rootPagesTotalCount, pageTotalCounts, articleTotalCounts, fileTotalCounts}) => ({
    currentArticles: (): KnowledgebaseContentResource[] => {
      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
      return articles().get(Number(idToCompare)) ?? [];
    },
    currentFiles: (): FileStorageResource[] => {
      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
      return files().get(Number(idToCompare)) ?? [];
    },
    currentCategory: (): KnowledgebasePageResource | undefined => {
      const currentPage = pageDetails().get(Number(routeData.categoryId()));

      if (currentPage) {
        return currentPage;
      }

      return undefined;
    },
    currentSubpagesTotalCount: (): number => {
      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? null : routeData.categoryId();
      return idToCompare === null ? rootPagesTotalCount() : (pageTotalCounts().get(idToCompare) ?? 0);
    },
    currentArticlesTotalCount: (): number => {
      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
      return articleTotalCounts().get(Number(idToCompare)) ?? 0;
    },
    currentFilesTotalCount: (): number => {
      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
      return fileTotalCounts().get(Number(idToCompare)) ?? 0;
    },
    currentSearchResultsTotalCount: (): number => {
      if (filter.type() === TYPE.KnowledgebaseFilterType.CATEGORY) {
        const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? null : routeData.categoryId();
        return idToCompare === null ? rootPagesTotalCount() : (pageTotalCounts().get(idToCompare) ?? 0);
      }

      if (filter.type() === TYPE.KnowledgebaseFilterType.ARTICLE) {
        const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
        return articleTotalCounts().get(Number(idToCompare)) ?? 0;
      }

      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
      return fileTotalCounts().get(Number(idToCompare)) ?? 0;
    },
    hideArticleEditorIcon: (): boolean => {
      const page = pageDetails().get(Number(routeData.categoryId()));
      if(page) {
        return page.hide_editor_icon;
      } else {
        return false;
      }
    },
    hasParentCategory: (): boolean => routeData.categoryId() !== null && routeData.categoryId() !== 0,
    hasMoreSearchDataToLoad: (): boolean => {
      if (filter.query().trim() === '') {
        return false;
      }

      const hasParent = routeData.categoryId() !== null && routeData.categoryId() !== 0;
      const categoryId = Number(routeData.categoryId() ?? 0);

      if (filter.type() === TYPE.KnowledgebaseFilterType.ARTICLE) {
        const pagination = articlesPagination().get(categoryId);
        return pagination ? !pagination.maxReached : true;
      }

      if (filter.type() === TYPE.KnowledgebaseFilterType.FILE) {
        const pagination = filesPagination().get(categoryId);
        return pagination ? !pagination.maxReached : true;
      }

      if (hasParent) {
        const pagination = pagePagination().get(categoryId);
        return pagination ? !pagination.maxReached : true;
      }

      return !rootPagination().maxReached;
    },
    hasMoreDataToLoad: (): boolean => {
      const hasParent = routeData.categoryId() !== null && routeData.categoryId() !== 0;
      if (hasParent) {
        const categoryId = Number(routeData.categoryId());
        const pagination = articlesPagination().get(categoryId);
        return pagination ? !pagination.maxReached : true;
      } else {
        return !rootPagination().maxReached;
      }
    },
  })),
  withMethods((store) => {
    const api: ApiService = inject(ApiService);
    const router: Router = inject(Router);
    const documentService: DocumentService = inject(DocumentService);

    return{
      resetStore(): void {
        patchState(store, {
          ...initialState,
          pages: new Map<number, KnowledgebaseSubPageResource[]>(),
          pageDetails: new Map<number, KnowledgebasePageResource>(),
          pagePagination: new Map<number, { limit: number, maxReached: boolean }>(),
          articles: new Map<number, KnowledgebaseContentResource[]>(),
          pageTotalCounts: new Map<number, number>(),
          articlesPagination: new Map<number, { skip: number, limit: number, maxReached: boolean }>(),
          articleTotalCounts: new Map<number, number>(),
          files: new Map<number, FileStorageResource[]>(),
          fileTotalCounts: new Map<number, number>(),
          filesPagination: new Map<number, { skip: number, limit: number, maxReached: boolean }>(),
          rootPagesLoading: false,
          pageLoading: new Map<number, boolean>(),
          articlesLoading: new Map<number, boolean>(),
          filesLoading: new Map<number, boolean>(),
        });
      },
      shouldLoadArticles(): boolean {
        if (store.filter.query() !== '') {
          return store.filter.type() === TYPE.KnowledgebaseFilterType.ARTICLE;
        }

        if (!store.hasParentCategory()) {
          return false;
        }

        return store.categoryContentFilter() === TYPE.KnowledgebaseCategoryContentFilter.ARTICLES_ONLY;
      },
      shouldLoadFiles(): boolean {
        if (store.filter.query() !== '') {
          return store.filter.type() === TYPE.KnowledgebaseFilterType.FILE;
        }

        if (!store.hasParentCategory()) {
          return false;
        }

        return store.categoryContentFilter() === TYPE.KnowledgebaseCategoryContentFilter.FILES_ONLY;
      },
      resetCurrentContentResults(): void {
        if (this.shouldLoadArticles()) {
          this.resetArticles();
        }

        if (this.shouldLoadFiles()) {
          this.resetFiles();
        }
      },
      loadCurrentContentResults(): void {
        if (this.shouldLoadArticles()) {
          this.loadArticlesForPage();
        }

        if (this.shouldLoadFiles()) {
          this.loadFilesForPage();
        }
      },
      updateFilterQuery(query: string): void {
        if (query === store.filter.query()) {
          return;
        }
        const previousQuery = store.filter.query();
        const isArticleOrFileSearch =
          store.filter.type() === TYPE.KnowledgebaseFilterType.ARTICLE ||
          store.filter.type() === TYPE.KnowledgebaseFilterType.FILE;
        const isCategorySearch = store.filter.type() === TYPE.KnowledgebaseFilterType.CATEGORY;
        const categoryContentOrder =
          isArticleOrFileSearch && previousQuery === '' && query !== ''
            ? TYPE.KnowledgebaseCategoryContentOrder.ALPHABETICAL
            : isArticleOrFileSearch && previousQuery !== '' && query === ''
              ? TYPE.KnowledgebaseCategoryContentOrder.PREDEFINED
              : store.categoryContentOrder();
        const categoryOrder =
          isCategorySearch && previousQuery === '' && query !== '' && !isAllowedCategorySearchOrder(store.categoryOrder())
            ? TYPE.KnowledgebaseCategoryOrder.ALPHABETICAL
            : store.categoryOrder();
        const fileView = getSearchResultFileView(
          query.trim() !== '',
          store.filter.type(),
          store.fileView()
        );
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            categoryOrder,
            categoryContentOrder,
            fileView,
            filter: {
              ...state.filter,
              query
            }
          })
        )
        this.resetPages();
        this.resetRootPages();
        this.loadCategories();
        this.resetArticles();
        this.resetFiles();
        this.loadCurrentContentResults();
      },
      updateFilterType(type: TYPE.KnowledgebaseFilterType): void {
        const hasSearchQuery = store.filter.query() !== '';
        const nextCategoryContentOrder =
          hasSearchQuery && (type === TYPE.KnowledgebaseFilterType.ARTICLE || type === TYPE.KnowledgebaseFilterType.FILE)
            ? TYPE.KnowledgebaseCategoryContentOrder.ALPHABETICAL
            : hasSearchQuery && (store.filter.type() === TYPE.KnowledgebaseFilterType.ARTICLE || store.filter.type() === TYPE.KnowledgebaseFilterType.FILE)
              ? TYPE.KnowledgebaseCategoryContentOrder.PREDEFINED
              : store.categoryContentOrder();
        const nextCategoryOrder =
          hasSearchQuery && type === TYPE.KnowledgebaseFilterType.CATEGORY && !isAllowedCategorySearchOrder(store.categoryOrder())
            ? TYPE.KnowledgebaseCategoryOrder.ALPHABETICAL
            : store.categoryOrder();
        const nextFileView = getSearchResultFileView(
          hasSearchQuery,
          type,
          store.fileView()
        );
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            categoryOrder: nextCategoryOrder,
            categoryContentOrder: nextCategoryContentOrder,
            fileView: nextFileView,
            filter: {
              ...state.filter,
              type
            }
          })
        );
        if(store.filter.query() !== '') {
          this.resetPages();
          this.resetRootPages();
          this.loadCategories();
          this.resetArticles();
          this.resetFiles();
          this.loadCurrentContentResults();
        }
      },
      updateCategoryOrder(categoryOrder: TYPE.KnowledgebaseCategoryOrder): void {
        const nextCategoryOrder =
          store.filter.type() === TYPE.KnowledgebaseFilterType.CATEGORY &&
          store.filter.query().trim() !== '' &&
          !isAllowedCategorySearchOrder(categoryOrder)
            ? TYPE.KnowledgebaseCategoryOrder.ALPHABETICAL
            : categoryOrder;
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            categoryOrder: nextCategoryOrder,
            rootPagination: initialState.rootPagination,
            pagePagination: initialState.pagePagination,
          })
        )
        this.reloadCategories();
        this.refreshQuickNavCurrentLevel();
      },
      updateCategoryContentOrder(categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder): void {
        if(categoryContentOrder !== store.categoryContentOrder()) {
          patchState(store, (state: KnowledgebaseState) => 
            ({
              ...state,
              categoryContentOrder
            })
          );
          this.resetArticles();
          this.resetFiles();
          this.loadCurrentContentResults();
          this.refreshQuickNavCurrentLevel();
        }
      },
      updateCategoryContentFilter(categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter): void {
        if (categoryContentFilter !== store.categoryContentFilter()) {
          patchState(store, (state: KnowledgebaseState) => 
            ({
              ...state,
              categoryContentFilter
            })
          );
          this.resetArticles();
          this.resetFiles();
          this.loadCurrentContentResults();
        }
      },
      resetCategoryContentSettings(): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder.PREDEFINED,
            categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter.ARTICLES_ONLY
          })
        )
      },
      updateRouteData(categoryId: number | null, articleId: number | null): void {
        if(categoryId !== store.routeData.categoryId() || articleId !== store.routeData.articleId()) {
          const shouldLoadCategory: boolean = categoryId !== store.routeData.categoryId();
          patchState(store, (state: KnowledgebaseState) => 
            ({
              ...state,
              routeData: {
                categoryId,
                articleId
              },
            })
          )
          if(shouldLoadCategory) {
            this.resetRootPages();
            this.resetPages();
            this.resetArticles();
            this.resetFiles();
            this.loadCategories();
            this.loadCurrentContentResults();
          }
        }
      },
      updatefileView(fileView: TYPE.KnowledgebaseFileViewType): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            fileView
          })
        );
      },
      toggleQuickNav(open?: boolean): void {
        patchState(store, (state: KnowledgebaseState) => {
          const nextOpen = open ?? !state.quickNavOpen;
          return {
            ...state,
            quickNavOpen: nextOpen,
            quickNavInitialized: nextOpen ? false : state.quickNavInitialized,
          };
        });
      },
      clearQuickNavErrors(): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            quickNavCategoriesError: null,
            quickNavArticlesError: null,
          })
        );
      },
      resetQuickNav(): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            quickNavInitialized: false,
            quickNavLevelCategoryId: null,
            quickNavCategoryOrderApplied: store.categoryOrder(),
            quickNavArticleOrderApplied: store.categoryContentOrder(),
            quickNavCategories: [],
            quickNavCategoriesLoading: false,
            quickNavCategoriesError: null,
            quickNavCategoriesLoadedAll: false,
            quickNavCategoriesSkip: 0,
            quickNavArticles: [],
            quickNavArticlesLoading: false,
            quickNavArticlesError: null,
            quickNavArticlesSkip: 0,
            quickNavArticlesHasMore: false,
          })
        );
      },
      loadQuickNavForLevel(categoryId: number | null): void {
        const levelId = categoryId && categoryId > 0 ? categoryId : null;
        const requestId = store.quickNavRequestId() + 1;
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            quickNavInitialized: true,
            quickNavRequestId: requestId,
            quickNavLevelCategoryId: levelId,
            quickNavCategoryOrderApplied: store.categoryOrder(),
            quickNavArticleOrderApplied: store.categoryContentOrder(),
            quickNavCategories: [],
            quickNavCategoriesLoading: true,
            quickNavCategoriesError: null,
            quickNavCategoriesLoadedAll: false,
            quickNavCategoriesSkip: 0,
            quickNavArticles: [],
            quickNavArticlesLoading: false,
            quickNavArticlesError: null,
            quickNavArticlesSkip: 0,
            quickNavArticlesHasMore: false,
          })
        );
        this.loadQuickNavCategoriesAll(levelId, requestId);
        if (levelId !== null) {
          this.loadQuickNavArticlesPage(levelId, requestId);
        }
      },
      refreshQuickNavCurrentLevel(): void {
        if (!store.quickNavOpen()) {
          return;
        }
        const levelId = store.quickNavLevelCategoryId();
        const requestId = store.quickNavRequestId() + 1;
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            quickNavInitialized: true,
            quickNavRequestId: requestId,
            quickNavLevelCategoryId: levelId,
            quickNavCategoryOrderApplied: store.categoryOrder(),
            quickNavArticleOrderApplied: store.categoryContentOrder(),
            quickNavCategories: [],
            quickNavCategoriesLoading: true,
            quickNavCategoriesError: null,
            quickNavCategoriesLoadedAll: false,
            quickNavCategoriesSkip: 0,
            quickNavArticles: [],
            quickNavArticlesLoading: false,
            quickNavArticlesError: null,
            quickNavArticlesSkip: 0,
            quickNavArticlesHasMore: false,
          })
        );
        this.loadQuickNavCategoriesAll(levelId, requestId);
        if (levelId !== null) {
          this.loadQuickNavArticlesPage(levelId, requestId);
        }
      },
      loadQuickNavCategoriesAll(categoryId: number | null, requestId: number): void {
        const limit = store.quickNavCategoriesLimit();
        const loadNext = (skip: number): void => {
          const apiCall = categoryId === null
            ? api.getKnowledgebaseRootPages(store.categoryOrder(), '', limit, skip)
            : api.getKnowledgebasePages(categoryId, store.categoryOrder(), '', limit, skip);
          apiCall.pipe(take(1)).subscribe({
            next: (response) => {
              if (store.quickNavRequestId() !== requestId) {
                return;
              }
              const merged = [
                ...store.quickNavCategories(),
                ...response.data
              ];
              const unique = Array.from(new Map(merged.map(item => [item.id, item])).values());
              const nextSkip = unique.length;
              patchState(store, (state: KnowledgebaseState) => 
                ({
                  ...state,
                  quickNavCategories: unique,
                  quickNavCategoriesSkip: nextSkip,
                })
              );
              if (response.moreEntries) {
                loadNext(nextSkip);
              } else {
                patchState(store, (state: KnowledgebaseState) => 
                  ({
                    ...state,
                    quickNavCategoriesLoading: false,
                    quickNavCategoriesLoadedAll: true,
                  })
                );
              }
            },
            error: () => {
              if (store.quickNavRequestId() !== requestId) {
                return;
              }
              patchState(store, (state: KnowledgebaseState) => 
                ({
                  ...state,
                  quickNavCategoriesLoading: false,
                  quickNavCategoriesError: 'Failed to load categories.',
                })
              );
            }
          });
        };
        loadNext(0);
      },
      loadQuickNavArticlesPage(categoryId: number, requestId?: number): void {
        const effectiveRequestId = requestId ?? store.quickNavRequestId();
        if (store.quickNavArticlesLoading()) {
          return;
        }
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            quickNavArticlesLoading: true,
            quickNavArticlesError: null,
          })
        );
        api.getKnowledgebasePageContent(
          categoryId,
          store.categoryContentOrder(),
          '',
          store.quickNavArticlesLimit(),
          store.quickNavArticlesSkip()
        )
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            if (store.quickNavRequestId() !== effectiveRequestId) {
              return;
            }
            const articles = response.data;
            const merged = [
              ...store.quickNavArticles(),
              ...articles
            ];
            const nextSkip = merged.length;
            const hasMore = nextSkip < response.totalCount;
            patchState(store, (state: KnowledgebaseState) => 
              ({
                ...state,
                quickNavArticles: merged,
                quickNavArticlesSkip: nextSkip,
                quickNavArticlesHasMore: hasMore,
                quickNavArticlesLoading: false,
              })
            );
          },
          error: () => {
            if (store.quickNavRequestId() !== effectiveRequestId) {
              return;
            }
            patchState(store, (state: KnowledgebaseState) => 
              ({
                ...state,
                quickNavArticlesLoading: false,
                quickNavArticlesError: 'Failed to load articles.',
              })
            );
          }
        });
      },
      scrollEndLoader(): void {
        if (this.shouldLoadArticles()) {
          this.loadArticlesForPage();
        } else if (this.shouldLoadFiles()) {
          this.loadFilesForPage();
        } else {
          this.loadRootPage();
        }
      },
      reloadCategories(): void {
        this.resetRootPages();
        this.resetPages();
        this.resetArticles();
        this.resetFiles();
        this.loadCategories();
        this.loadCurrentContentResults();
      },
      loadCategories(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          categoriesLoading: true,
        }));
        if(store.routeData.categoryId() === null || store.routeData.categoryId() === 0) {
          this.loadRootPage();
        } else {
          this.loadPageDetails(store.routeData.categoryId() as number);
          this.loadPage(store.routeData.categoryId() as number);
        }
        this.resetCategoryTree();
      },
      loadRootPage(): void {
        if(store.rootPagination.maxReached() === false && !store.rootPagesLoading()) {
          patchState(store, (state: KnowledgebaseState) => ({
            ...state,
            rootPagesLoading: true,
          }));
          api.getKnowledgebaseRootPages(
            store.categoryOrder(),
            store.filter.type() === TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() :'',
            store.rootPagination.limit(),
            store.rootPages().length > 0 ? store.rootPages().length : null)
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              const rootPages = response.data;
              const moreEntries = response.moreEntries;
              const totalCount = response.totalCount;
              patchState(store, (state: KnowledgebaseState) => {
                // Merge existing and loaded root pages, remove duplicates by id
                const mergedPages = [
                ...state.rootPages,
                ...rootPages
                ];
                const uniquePages = Array.from(
                new Map(mergedPages.map(page => [page.id, page])).values()
                );
                return {
                ...state,
                categoriesLoading: false,
                rootPagesLoading: false,
                rootPages: uniquePages,
                rootPagesTotalCount: totalCount,
                rootPagination: {
                  ...state.rootPagination,
                  maxReached: uniquePages.length >= totalCount || !moreEntries,
                }
                };
              });
            },
            error: (err) => {
              patchState(store, (state: KnowledgebaseState) => ({
                ...state,
                categoriesLoading: false,
                rootPagesLoading: false,
              }));
              console.error('Error loading root pages');
              console.error(err);
            }
          });
        }
      },
      loadPage(pageId: number): void {
        if(store.pagePagination().get(pageId) === undefined) {
          this.createPagePagination(pageId);
        }
        const pageLoadInProgress = store.pageLoading().get(pageId) ?? false;
        if(store.pagePagination().get(pageId)?.maxReached === false && !pageLoadInProgress) {
            patchState(store, (state: KnowledgebaseState) => {
              const updatedPageLoading = new Map(state.pageLoading);
              updatedPageLoading.set(pageId, true);
              return {
                ...state,
                pageLoading: updatedPageLoading,
              };
            });

            api.getKnowledgebasePages(
            pageId,
            store.categoryOrder(),
            store.filter.type() === TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() : '',
            store.pagePagination().get(pageId)!.limit ?? 10,
            store.pages().get(pageId)?.length ?? null
            )
            .pipe(take(1))
            .subscribe({
            next: (response) => {
              const loadedSubpages = response.data;
              const moreEntries = response.moreEntries;
              const totalCount = response.totalCount;
              let mergedSubpagesCount = 0;
              patchState(store, (state: KnowledgebaseState) => {
                const updatedPages = new Map(state.pages);
                const updatedPageTotalCounts = new Map(state.pageTotalCounts);
                const existingSubpages = updatedPages.get(pageId) ?? [];
                const mergedSubpages = Array.from(
                  new Map(
                    [...existingSubpages, ...loadedSubpages].map((subpage) => [subpage.id, subpage])
                  ).values()
                );
                mergedSubpagesCount = mergedSubpages.length;
                updatedPages.set(pageId, mergedSubpages);
                updatedPageTotalCounts.set(pageId, totalCount);
                return {
                  ...state,
                  categoriesLoading: false,
                  pages: updatedPages,
                  pageTotalCounts: updatedPageTotalCounts
                };
              });
              // Update pagePagination for the current page
              const currentPagination = store.pagePagination().get(pageId);
              const newMaxReached = mergedSubpagesCount >= totalCount || !moreEntries;
              const updatedPagePagination = new Map(store.pagePagination());
              updatedPagePagination.set(pageId, {
              ...currentPagination,
              maxReached: newMaxReached,
              limit: currentPagination?.limit ?? 10,
              });

              patchState(store, (state: KnowledgebaseState) => ({
              ...state,
              pagePagination: updatedPagePagination,
              }));
              patchState(store, (state: KnowledgebaseState) => {
                const updatedPageLoading = new Map(state.pageLoading);
                updatedPageLoading.set(pageId, false);
                return {
                  ...state,
                  pageLoading: updatedPageLoading,
                };
              });
            },
            error: (err) => {
              patchState(store, (state: KnowledgebaseState) => {
                const updatedPageLoading = new Map(state.pageLoading);
                updatedPageLoading.set(pageId, false);
                return {
                  ...state,
                  categoriesLoading: false,
                  pageLoading: updatedPageLoading,
                };
              });
              console.error('Error loading pages for page ID: ', pageId);
              console.error(err);
            }
            });
        }
      },
      loadPageDetails(pageId: number): void {
        api.getKnowledgebasePageDetails(pageId)
        .pipe(take(1))
        .subscribe({
          next: (pageDetails: KnowledgebasePageResource) => {
            patchState(store, (state: KnowledgebaseState) => {
              const updatedPageDetails = new Map(state.pageDetails);
              updatedPageDetails.set(pageId, pageDetails);
              return {
                ...state,
                pageDetails: updatedPageDetails,
              };
            });
          },
          error: (err) => {
            console.error('Error loading page details for page ID: ', pageId);
            console.error(err);
          }
        });
      },
      loadArticlesForPage(): void {
        if (!this.shouldLoadArticles()) {
          return;
        }

        const categoryId = Number(store.routeData.categoryId() ?? 0);
        if(store.articlesPagination().get(categoryId) === undefined) {
          this.createArticlePagination(categoryId);
        }
        const articlesLoadInProgress = store.articlesLoading().get(categoryId) ?? false;
        if( store.articlesPagination().get(categoryId)?.maxReached === false && !articlesLoadInProgress) {
          patchState(store, (state: KnowledgebaseState) => {
            const updatedArticlesLoading = new Map(state.articlesLoading);
            updatedArticlesLoading.set(categoryId, true);
            return {
              ...state,
              articlesLoading: updatedArticlesLoading,
            };
          });
          api.getKnowledgebasePageContent(
            categoryId,
            store.categoryContentOrder(),
            store.filter.type() !== TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() :'',
            store.articlesPagination().get(categoryId)!.limit ?? 50,
            store.articlesPagination().get(categoryId)!.skip ?? 0
          ).pipe(take(1))
          .subscribe({
            next: (response) => {
              const articles = response.data;
              const totalCount = response.totalCount;
              const updatedArticles = new Map(store.articles());
                const existingArticles = updatedArticles.get(categoryId) ?? [];
                const newArticlesMap = new Map(articles.map(a => [a.id, a]));
                const mergedArticles = [
                ...existingArticles.filter(a => !newArticlesMap.has(a.id)),
                ...articles
              ];
                updatedArticles.set(categoryId, mergedArticles);
              const updatedArticleTotalCounts = new Map(store.articleTotalCounts());
              updatedArticleTotalCounts.set(categoryId, totalCount);
              patchState(store, (state: KnowledgebaseState) => ({
                ...state,
                articles: updatedArticles,
                articleTotalCounts: updatedArticleTotalCounts,
              }));
              const currentPagination = store.articlesPagination().get(categoryId);
              const newSkip = (currentPagination?.skip ?? 0) + (currentPagination?.limit ?? 50);
              const newMaxReached = mergedArticles.length >= totalCount;
              const updatedArticlesPagination = new Map(store.articlesPagination());
              updatedArticlesPagination.set(categoryId, {
                ...currentPagination,
                skip: newSkip,
                maxReached: newMaxReached,
                limit: currentPagination?.limit ?? 50,
              });

              patchState(store, (state: KnowledgebaseState) => ({
                ...state,
                articles: updatedArticles,
                articlesPagination: updatedArticlesPagination,
              }));
              patchState(store, (state: KnowledgebaseState) => {
                const updatedArticlesLoading = new Map(state.articlesLoading);
                updatedArticlesLoading.set(categoryId, false);
                return {
                  ...state,
                  articlesLoading: updatedArticlesLoading,
                };
              });
            },
            error: (err) => {
              patchState(store, (state: KnowledgebaseState) => {
                const updatedArticlesLoading = new Map(state.articlesLoading);
                updatedArticlesLoading.set(categoryId, false);
                return {
                  ...state,
                  articlesLoading: updatedArticlesLoading,
                };
              });
              console.error('Error loading articles for page ID: ', categoryId);
              console.error(err);
            }
          });
        }
      },
      loadFilesForPage(): void {
        if (!this.shouldLoadFiles()) {
          return;
        }

        const categoryId = Number(store.routeData.categoryId() ?? 0);
        if(store.filesPagination().get(categoryId) === undefined) {
          this.createFilePagination(categoryId);
        }
        const filesLoadInProgress = store.filesLoading().get(categoryId) ?? false;
        if(store.filesPagination().get(categoryId)?.maxReached === false && !filesLoadInProgress) {
          patchState(store, (state: KnowledgebaseState) => {
            const updatedFilesLoading = new Map(state.filesLoading);
            updatedFilesLoading.set(categoryId, true);
            return {
              ...state,
              filesLoading: updatedFilesLoading,
            };
          });
          api.getKnowledgebasePageContentFiles(
            categoryId,
            store.categoryContentOrder(),
            store.filter.type() !== TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() :'',
            store.filesPagination().get(categoryId)!.limit ?? 50,
            store.filesPagination().get(categoryId)!.skip ?? 0
          ).pipe(take(1))
          .subscribe({
            next: (response) => {
              const files = response.data;
              const totalCount = response.totalCount;
              const updatedFiles = new Map(store.files());
              const existingFiles = updatedFiles.get(categoryId) ?? [];
              const newFilesMap = new Map(files.map(f => [f.uuid, f]));
              const mergedFiles = [
                ...existingFiles.filter(f => !newFilesMap.has(f.uuid)),
                ...files
              ];
              updatedFiles.set(categoryId, mergedFiles);
              const updatedFileTotalCounts = new Map(store.fileTotalCounts());
              updatedFileTotalCounts.set(categoryId, totalCount);
              patchState(store, (state: KnowledgebaseState) => ({
                ...state,
                files: updatedFiles,
                fileTotalCounts: updatedFileTotalCounts,
              }));
              const currentPagination = store.filesPagination().get(categoryId);
              const newSkip = (currentPagination?.skip ?? 0) + (currentPagination?.limit ?? 50);
              const newMaxReached = mergedFiles.length >= totalCount;
              const updatedFilesPagination = new Map(store.filesPagination());
              updatedFilesPagination.set(categoryId, {
                ...currentPagination,
                skip: newSkip,
                maxReached: newMaxReached,
                limit: currentPagination?.limit ?? 50,
              });

              patchState(store, (state: KnowledgebaseState) => ({
                ...state,
                files: updatedFiles,
                filesPagination: updatedFilesPagination,
              }));
              patchState(store, (state: KnowledgebaseState) => {
                const updatedFilesLoading = new Map(state.filesLoading);
                updatedFilesLoading.set(categoryId, false);
                return {
                  ...state,
                  filesLoading: updatedFilesLoading,
                };
              });
            },
            error: (err) => {
              patchState(store, (state: KnowledgebaseState) => {
                const updatedFilesLoading = new Map(state.filesLoading);
                updatedFilesLoading.set(categoryId, false);
                return {
                  ...state,
                  filesLoading: updatedFilesLoading,
                };
              });
              console.error('Error loading files for page ID: ', categoryId);
              console.error(err);
            }
          });
        }
      },
      createArticlePagination(categoryId: number): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          articlesPagination: state.articlesPagination.set(categoryId, {
            skip: 0,
            limit: 50,
            maxReached: false,
          })
        }));
      },
      createFilePagination(categoryId: number): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          filesPagination: state.filesPagination.set(categoryId, {
            skip: 0,
            limit: 50,
            maxReached: false,
          })
        }));
      },
      createPagePagination(pageId: number): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          pagePagination: state.pagePagination.set(pageId, {
            limit: 10,
            maxReached: false,
          })
        }));
      },
      loadSpecificCategory(categoryID: number): Observable<KnowledgebasePageAdminResource | null> {
        return new Observable<KnowledgebasePageAdminResource | null>((observer) => {
          api.getKnowledgebasePageById(categoryID)
          .pipe(take(1))
          .subscribe({
            next: (category: KnowledgebasePageAdminResource) => {
              observer.next(category);
              observer.complete();
            },
            error: (err) => {
              console.error('Error loading specific category for category ID: ', categoryID);
              console.error(err);
              observer.next(null);
              observer.complete();
            }
          });
        });
      },
      loadSpecificArticle(articleID: number): Observable<KnowledgebaseContentResource | null> {
        return new Observable<KnowledgebaseContentResource | null>((observer) => {
          api.getKnowledgebasePageContentById(articleID)
          .pipe(take(1))
          .subscribe({
            next: (category: KnowledgebaseContentResource) => {
              observer.next(category);
              observer.complete();
            },
            error: (err) => {
              console.error('Error loading specific article for article ID: ', articleID);
              console.error(err);
              observer.next(null);
              observer.complete();
            }
          });
        });
      },
      resetCategoryTree(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          categoryTree: {
            entries: [],
            searchString: '',
          }
        }));
      },
      changeCategoryTreeSearchString(searchString: string): void {
        this.resetCategoryTree();
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          categoryTree: {
            ...state.categoryTree,
            searchString: searchString
          }
        }));
      },
      loadCategoryTree(): void {
          api.getKnowledgebasePageTree(store.categoryTree().searchString)
          .pipe(take(1))
          .subscribe({
            next: (categoryTree: KnowledgebasePageTreeResource[]) => {
              patchState(store, (state: KnowledgebaseState) => ({
                ...state,
                categoryTree: {
                  ...state.categoryTree,
                  entries: categoryTree
                }
              }));
            },
            error: (err) => { 
              console.error('Error loading category tree with search string: ', store.categoryTree().searchString);
              console.error(err);
            }
          });
      },
      toggleCategoryIsFavorite(data: {categoryId: number, important: boolean}): void {
        api.setKnowledgebasePageFavorite(data.categoryId, data.important)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.reloadCategories();
            this.refreshQuickNavCurrentLevel();
          },
          error: () => {
            console.error('Error toggling category favorite status for category ID: ', data);
          }
        });
      },
      deleteCategory(categoryID: number): void {
        api.deleteKnowledgebasePage(categoryID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.loadCategories();
            this.refreshQuickNavCurrentLevel();
          },
          error: () => {
            console.error('Error deleting category for category ID: ', categoryID);
          }
        });
      },
      createUpdateCategory(category: KnowledgebasePageUpdateRequest, existingID: number | null): void {
        api.createOrUpdateKnowledgebasePage(category, existingID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.loadCategories();
            this.refreshQuickNavCurrentLevel();
          },
          error: (err) => {
            console.error('Error creating/updating category: ', category);
            console.error(err);
          }
        });
      },
      updatePageOrder(pageID: number, direction: KnowledgebaseMoveDirection): void {
        api.updateKnowledgebasePageOrder(pageID, {direction: direction})
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.loadCategories();
            this.refreshQuickNavCurrentLevel();
          },
          error: (err) => {
            console.error('Error updating category order for category ID: ', pageID, ' and direction: ', direction);
            console.error(err);
          }
        });
      },
      createUpdateArticle(article: KnowledgebaseContentUpdateRequest, existingID: number | null): void {
        api.createOrUpdateKnowledgebasePageContent(article, existingID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
            this.refreshQuickNavCurrentLevel();
          },
          error: (err) => {
            console.error('Error creating/updating article: ', article);
            console.error(err);
          }
        });
      },
      moveArticleLeft(articleID: number): void {
        api.updateKnowledgebasePageContentOrder(articleID, {direction: KnowledgebaseMoveDirection.Up})
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
            this.refreshQuickNavCurrentLevel();
          },
          error: (err) => {
            console.error('Error moving article left for article ID: ', articleID);
            console.error(err);
          }
        });
      },
      moveArticleRight(articleID: number): void {
        api.updateKnowledgebasePageContentOrder(articleID, {direction: KnowledgebaseMoveDirection.Down})
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
            this.refreshQuickNavCurrentLevel();
          },
          error: (err) => {
            console.error('Error moving article right for article ID: ', articleID);
            console.error(err);
          }
        });
      },
      setArticleFavorite(articleID: number, important: boolean): void {
        api.setKnowledgebasePageContentFavorite(articleID, important)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
          },
          error: (err) => {
            console.error('Error toggling article favorite status for article ID: ', articleID, ' and important: ', important);
            console.error(err);
          }
        });
      },
      markKnowledgebaseArticleRead(articleID: number, articleReloaded?: (article: KnowledgebaseContentResource) => void): void {
        const article = store.currentArticles().find((item) => item.id === articleID);
        if (!article || article.read) {
          return;
        }

        api.markKnowledgebasePageContentRead(articleID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.loadSpecificArticle(articleID)
            .pipe(take(1))
            .subscribe((loadedArticle) => {
              if (!loadedArticle) {
                return;
              }

              this.updateArticleInStore(loadedArticle);
              articleReloaded?.(loadedArticle);
            });
          },
          error: (err) => {
            console.error('Error marking article read for article ID: ', articleID);
            console.error(err);
          }
        });
      },
      deleteArticle(articleID: number): void {
        api.deleteKnowledgebasePageContent(articleID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
            this.refreshQuickNavCurrentLevel();
          },
          error: () => {
            console.error('Error deleting article for article ID: ', articleID);
          }
        });
      },
      updateArticleReadCount(articleId: number, readCount: number | null): void {
        const updatedArticles = new Map<number, KnowledgebaseContentResource[]>();
        store.articles().forEach((articles, categoryId) => {
          const mappedArticles = articles.map((article) =>
            article.id === articleId ? {...article, read_count: readCount} : article
          );
          updatedArticles.set(categoryId, mappedArticles);
        });
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          articles: updatedArticles,
        }));
      },
      updateArticleInStore(updatedArticle: KnowledgebaseContentResource): void {
        const updatedArticles = new Map<number, KnowledgebaseContentResource[]>();
        store.articles().forEach((articles, categoryId) => {
          const mappedArticles = articles.map((article) =>
            article.id === updatedArticle.id ? updatedArticle : article
          );
          updatedArticles.set(categoryId, mappedArticles);
        });

        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          articles: updatedArticles,
        }));
      },
      getArticleReaders(query: string, limit: number, skip: number, articleId: number): Observable<UserMinimalResource[]> {
        return new Observable<UserMinimalResource[]>((observer) => {
          api.getKnowledgebasePageReaders(articleId, query, limit, skip)
          .pipe(take(1))
          .subscribe({
            next: (readers: UserMinimalResource[]) => {
              observer.next(readers);
              observer.complete();
            },
            error: (err) => {
              console.error('Error loading article readers for article ID: ', articleId);
              console.error(err);
              observer.next([]);
              observer.complete();
            }
          });
        });
      },
      moveArticleToCategory(articleID: number, newParentID: number | null): void {
        this.loadSpecificArticle(articleID)
        .pipe(take(1))
        .subscribe({
          next: (article) => {
            const mappedArticle: KnowledgebaseContentUpdateRequest = {
              parent_id: newParentID,
              title: article?.title || '',
              content: article?.content || '',
              content_media_uuid: article?.content_media?.uuid || null,
              notify_important: article?.favorite || false,
              timetable_start: article?.timetable_start || null,
              timetable_dead: article?.timetable_dead || null,
              file_storage_uuids: article?.files.map((x) => x.uuid) || [],
              notify_users: false, // No need to notify users when just moving an article, as there are no content changes
            };
            this.createUpdateArticle(mappedArticle, articleID);
          },
          error: (err) => {
            console.error('Error loading article for moving article ID: ', articleID, ' to category ID: ', newParentID);
            console.error(err);
          }
        });
      },
      moveCategory(categoryID: number, newParentID: number | null): void {
        this.loadSpecificCategory(categoryID)
        .pipe(take(1))
        .subscribe({
          next: (category) => {
            const mappedCategory: KnowledgebasePageUpdateRequest = {
              name: category?.name || '',
              description: category?.description || '',
              parent_id: newParentID,
              hide_editor_icon: category?.hide_editor_icon || false,
              link: category?.link || null,
              file_storage_uuid: category?.image?.uuid || null,
              editor_users: category?.editor_users || [],
              editor_groups: category?.editor_groups || [],
              viewer_groups: category?.viewer_groups || [],
            };
            this.createUpdateCategory(mappedCategory, categoryID);
          },
          error: (err) => {
            console.error('Error loading category for moving category ID: ', categoryID, ' to category ID: ', newParentID);
            console.error(err);
          }
        });
      },
      exportArticles():void {
        api.exportKnowledgebasePage(store.routeData.categoryId() as number)
        .pipe(take(1))
        .subscribe({
          next: (response: HttpResponse<string | ArrayBuffer>) => {
            const data: string | ArrayBuffer | null = response.body;
            if(data !== null) {
              let filename = 'HMSapp_Page_' + (store.routeData.categoryId() ?? 'unknown') + '_export.val';
              if (response.headers.has('content-disposition')) {
                const contentDisposition = response.headers.get('content-disposition') as string;
                const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i);
                if (filenameMatch?.[1]) {
                  filename = decodeURIComponent(filenameMatch[1]);
                }
              }
              documentService.downloadData(data, filename, 'application/gzip');
            } else {
              throw Error('Export response for knowledgebase export was null');
            }
          },
          error: (err) => {            
            console.error('Error exporting articles for category ID: ', store.routeData.categoryId());
            console.error(err);
          } 
        });
      },
      importArticles(document: FileStorageResource): void {
        api.importKnowledgebasePage(store.routeData.categoryId() ?? 0, document.uuid)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
            this.loadCategories();
          },
          error: (err) => {
            console.error('Error importing articles from document: ', document);
            console.error(err);
          }
        });
      },
      resetRootPages(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          rootPages: [],
          rootPagesTotalCount: 0,
          rootPagination: {
            ...initialState.rootPagination,
          },
          rootPagesLoading: false,
        }));
      },
      resetPages(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          pages: new Map<number, KnowledgebaseSubPageResource[]>(),
          pageTotalCounts: new Map<number, number>(),
          pageDetails: new Map<number, KnowledgebasePageResource>(),
          pagePagination: new Map<number, { limit: number, maxReached: boolean }>(),
          pageLoading: new Map<number, boolean>(),
        }));
      },
      resetArticles(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          articles: new Map<number, KnowledgebaseContentResource[]>(),
          articleTotalCounts: new Map<number, number>(),
          articlesPagination: new Map<number, { skip: number, limit: number, maxReached: boolean }>(),
          articlesLoading: new Map<number, boolean>(),
        }));
      },
      resetFiles(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          files: new Map<number, FileStorageResource[]>(),
          fileTotalCounts: new Map<number, number>(),
          filesPagination: new Map<number, { skip: number, limit: number, maxReached: boolean }>(),
          filesLoading: new Map<number, boolean>(),
        }));
      },
      natigateToCategory(categoryId: number): void {
        const currentCategoryId = store.routeData.categoryId();
        const subpage = store.hasParentCategory()
          ? (store.pages().get(Number(currentCategoryId)) ?? []).find((sub) => sub.id === categoryId)
          : store.rootPages().find((sub) => sub.id === categoryId);

        if(subpage) {
          if(subpage.link !== null) {
            window.open(subpage.link, '_blank');
          } else {
            this.updateFilterQuery('');
            router.navigate(['/knowledgebase', categoryId]);
            this.resetCategoryContentSettings();
          }
        } else {
          this.updateFilterQuery('');
          router.navigate(['/knowledgebase', categoryId]);
          this.resetCategoryContentSettings();
        }
      },
      openFile(file: FileStorageResource): void {
        documentService.downloadDocument(file);
      }
  }
  }),
  withComputed((store) => ({
    currentSubpages: computed((): KnowledgebaseSubPageResource[] => {
      const idToCompare = (store.routeData.categoryId() === null || store.routeData.categoryId() === 0) ? null : store.routeData.categoryId();
      if(idToCompare === null) {
        return store.rootPages();
      } else {
        return store.pages().get(idToCompare) ?? [];
      }
    })
  })),
)
